import * as React from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { TR } from 'src/TextRes';
import { ApiBasedataList, ApiBasedataSave, BasedataParams, ServerResponse } from '../../api';
import Footer from '../../components/footer/Footer';
import HeaderLoggedIn from '../../components/headerLoggedIn/HeaderLoggedIn';
import ItemEditableList from '../../components/itemEditableList/ItemEditableList';
import NetworkOverlay from '../../components/networkOverlay/NetworkOverlay';
import { IdataListProps, IEmptyProps, IItemEditableListState } from '../../constants';
import './AdminBaseData.css';

interface IState {
    lists: IItemEditableListState[];
    loading: boolean;
    currentEditItemID: number | undefined;
    currentDeleteItemID: number;
    currentDeleteListIndex: number;
    modalOpen: boolean;
    warningModalOpen: boolean;
    editValue: string | undefined;
    modalState: string;
    serverResponse?: ServerResponse;
}

export default class BaseDataView extends React.PureComponent<IEmptyProps, IState> {
    constructor(props: IEmptyProps) {
        super(props);
        this.state = {
            currentDeleteItemID: 0,
            currentDeleteListIndex: 0,
            currentEditItemID: undefined,
            editValue: '',
            lists: [],
            loading: true,
            modalOpen: false,
            modalState: 'none',
            serverResponse: undefined,
            warningModalOpen: false,
        };
    }

    public componentDidMount() {
        this.getLists();
    }

    public render() {
        return (
            <NetworkOverlay loading={this.state.loading} serverResponse={this.state.serverResponse}>
                <HeaderLoggedIn />
                {this.state.lists.map((data, index) => {
                    return (
                        <ItemEditableList
                            addNewRecord={this.addNewRecord}
                            editHandler={this.editHandler}
                            selectEntry={this.selectEntry}
                            saveHandler={this.saveHandler}
                            cancelHandler={this.cancelHandler}
                            changeHandler={this.changeHandler}
                            editItemID={this.state.currentEditItemID}
                            data={data.data}
                            baseDataKey={data.key}
                            key={index}
                            listIndex={index}
                            name={data.name}
                            editValue={this.state.editValue}
                        />
                    );
                })}
                <Modal isOpen={this.state.modalOpen}>
                    <ModalHeader>{TR.adminBaseData_delete_entry}</ModalHeader>
                    <ModalBody>{TR.adminBaseData_really_delete}</ModalBody>
                    <ModalFooter>
                        <Button
                            color="primary"
                            onClick={() =>
                                this.deleteHandler(this.state.currentDeleteListIndex, this.state.currentDeleteItemID)
                            }
                        >
                            {TR.adminBaseData_delete}
                        </Button>{' '}
                        <Button color="secondary" onClick={() => this.toggleModal('none')}>
                            {TR.adminBaseData_cancel}
                        </Button>
                    </ModalFooter>
                </Modal>
                <Modal isOpen={this.state.warningModalOpen}>
                    <ModalHeader>{TR.adminBaseData_save_entry}</ModalHeader>
                    <ModalBody>{TR.adminBaseData_already_exists}</ModalBody>
                    <ModalFooter>
                        <Button color="primary" onClick={() => this.setState({ warningModalOpen: false })}>
                            {TR.adminBaseData_ok}
                        </Button>
                    </ModalFooter>
                </Modal>
                <Footer />
            </NetworkOverlay>
        );
    }

    private addNewRecord = (newRecord: string, listIndex: number) => {
        const updatedLists = this.state.lists.slice(0); // clone the array
        const idArray: number[] = [];
        this.state.lists.map((data: IItemEditableListState) => {
            data.data.map((d: IdataListProps) => {
                idArray.push(d.id);
            });
        });

        const contentArray: string[] = [];
        this.state.lists[listIndex].data.map((d: IdataListProps) => {
            contentArray.push(d.content.toLowerCase());
        });

        if (contentArray.indexOf(newRecord.toLowerCase()) < 0) {
            updatedLists[listIndex].data.push({
                content: newRecord,
                id: Math.max(...idArray) + 1,
            });
            this.setState({ lists: updatedLists }, () => {
                const myList: IItemEditableListState = this.state.lists[listIndex];
                const changedBasedataEntries = myList.data.map((entry) => ({ Content: entry.content, ID: entry.id }));
                const changedBasedata: BasedataParams = {
                    Key: myList.key,
                    Name: myList.name,
                    Data: changedBasedataEntries,
                };
                ApiBasedataSave({ Basedata: changedBasedata }, (sr) => {
                    if (sr.Data !== undefined) {
                        this.translateToStateLists(sr.Data.Basedata);
                        this.setState({ modalOpen: false, modalState: 'none' });
                    } else {
                        this.setState({ loading: false });
                    }
                });
            });
        } else {
            this.setState({ warningModalOpen: true });
        }
    };

    private editHandler = (entryId: number, value: string) => {
        this.setState({ currentEditItemID: entryId, editValue: value });
    };

    private getLists = () => {
        ApiBasedataList((sr) => {
            if (sr.Data !== undefined) {
                this.translateToStateLists(sr.Data.Basedata);
            } else {
                this.setState({ serverResponse: sr });
            }
        });
    };

    private deleteHandler = (listIndex: number, entryId: number) => {
        const baseDataOld: IItemEditableListState = this.state.lists[listIndex];
        const baseDataEntriesOld = baseDataOld.data.filter((entry) => entry.id !== entryId);

        const changedBasedataEntries = baseDataEntriesOld.map((entry) => ({ Content: entry.content, ID: entry.id }));
        const changedBasedata: BasedataParams = {
            Data: changedBasedataEntries,
            Key: baseDataOld.key,
            Name: baseDataOld.name,
        };

        ApiBasedataSave({ Basedata: changedBasedata }, (sr) => {
            if (sr.Data !== undefined) {
                this.translateToStateLists(sr.Data.Basedata);
                this.setState({ modalOpen: false, modalState: 'none' });
            } else {
                this.setState({ loading: false });
            }
        });
    };

    private selectEntry = (currentDeleteListIndex: number, currentDeleteItemID: number, selectionMethod: string) => {
        if (selectionMethod === 'trash') {
            this.setState({
                currentDeleteItemID,
                currentDeleteListIndex,
                modalOpen: true,
                modalState: selectionMethod,
            });
        }
    };

    private translateToStateLists = (data: BasedataParams[]) => {
        const newLists: IItemEditableListState[] = data.map((basedata) => ({
            data: basedata.Data.map((entry) => ({ content: entry.Content, id: entry.ID })),
            key: basedata.Key,
            name: basedata.Name,
        }));
        this.setState({ lists: newLists, loading: false });
    };

    private saveHandler = (listIndex: number, entryId: number) => {
        // checking if we have the same entry
        const contentArray: string[] = [];
        this.state.lists[listIndex].data.map((d: IdataListProps) => {
            contentArray.push(d.content.toLowerCase());
        });

        // Get the list from the state and modify its relevant entry
        const listData: IItemEditableListState = this.state.lists[listIndex];
        listData.data = listData.data.map((entry) => {
            if (
                entry.id === entryId &&
                this.state.editValue !== undefined &&
                contentArray.indexOf(this.state.editValue.toLowerCase()) < 0
            ) {
                return { content: this.state.editValue, id: entry.id };
            } else if (this.state.editValue && contentArray.indexOf(this.state.editValue.toLowerCase()) > -1) {
                this.setState({ warningModalOpen: true });
                return entry;
            } else {
                return entry;
            }
        });

        // Structure it according to Basedata interface
        const changedBasedata = {
            Data: listData.data.map((entry) => ({ Content: entry.content, ID: entry.id })),
            Key: listData.key,
            Name: listData.name,
        };

        // Send it to the server
        if (this.state.editValue && contentArray.indexOf(this.state.editValue.toLowerCase()) < 0) {
            ApiBasedataSave({ Basedata: changedBasedata }, (sr) => {
                if (sr.Data !== undefined) {
                    this.translateToStateLists(sr.Data.Basedata);
                    this.setState({
                        modalOpen: false,
                        modalState: 'none',
                        currentEditItemID: undefined,
                        editValue: '',
                    });
                } else {
                    this.setState({ loading: false });
                }
            });
        }
    };

    private cancelHandler = () => {
        this.setState({ currentEditItemID: undefined });
    };

    private changeHandler = (editValue: string) => {
        this.setState({ editValue });
    };

    private toggleModal = (modalState: string) => {
        this.setState({ modalOpen: !this.state.modalOpen, modalState });
    };
}



// WEBPACK FOOTER //
// ./src/views/adminBaseData/AdminBaseData.tsx